/**
 * Drops every "no override" leaf from a theme payload, recursively.
 *
 * This is the load-bearing piece of the whole override layer. An absent leaf falls back to the
 * tenant's static baseline through the `var(--peli-…, <baseline>)` fallback baked into the Chakra
 * theme; a leaf saved as an empty string does not — `--peli-x: ;` is invalid at computed-value
 * time, so the property resolves to nothing at all and can render text transparent.
 * `flattenThemeToCssVars` in packages/utils/src/models/customer-theme/css-vars.ts omits empties
 * for exactly that reason, and this is the write-side half of the same rule: clearing a field has
 * to delete the key, not blank it, or the change is un-undoable from the UI.
 *
 * Objects that end up empty are dropped too, so a section the user cleared disappears entirely and
 * the schema's `.default({})` puts the empty shape back on read.
 *
 * Arrays are walked as plain objects on purpose. react-hook-form's `set()` replaces a missing
 * parent with an array when the next path segment is numeric, so a colour scale can arrive as a
 * sparse array keyed 50…900 rather than an object; `Object.entries` skips the holes, which turns
 * it back into the object the schema expects.
 */
export const pruneEmpty = (value: unknown): unknown => {
  if (value === null || value === undefined) return undefined;

  if (typeof value === 'string') {
    const trimmed = value.trim();

    return trimmed.length === 0 ? undefined : trimmed;
  }

  if (typeof value !== 'object') return value;

  const entries = Object.entries(value as Record<string, unknown>)
    .map(([key, child]) => [key, pruneEmpty(child)] as const)
    .filter(([, child]) => child !== undefined);

  return entries.length === 0 ? undefined : Object.fromEntries(entries);
};
